package com.mgacreative.hotel.feature.inventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.lazy.rememberLazyListState
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotel.domain.repository.InventoryItem
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

enum class InventoryStatus(val color: Color, val bgColor: Color, val text: String) {
    IN_STOCK(Color(0xFF16A34A), Color(0xFFDCFCE7), "Stokta"),
    LOW_STOCK(Color(0xFFD97706), Color(0xFFFEF3C7), "Kritik Stok"),
    OUT_OF_STOCK(Color(0xFFDC2626), Color(0xFFFEE2E2), "Tükendi")
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun InventoryScreen(
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit = {}
) {
    val viewModel = koinViewModel<InventoryViewModel>()
    val purchasingViewModel = koinViewModel<PurchasingViewModel>()
    val inventoryItems by viewModel.items.collectAsState()

    var currentTab by remember { mutableStateOf(0) } // 0: Stocks, 1: Orders
    var showAddDialog by remember { mutableStateOf(false) }
    var selectedCategoryFilter by remember { mutableStateOf("ALL") }
    var searchQuery by remember { mutableStateOf("") }

    val stockListState = rememberLazyListState()

    val totalItems = inventoryItems.size
    val criticalStockItems = inventoryItems.count { it.quantity <= it.min_threshold && it.quantity > 0 }
    val outOfStockItems = inventoryItems.count { it.quantity <= 0 }

    val filteredItems = inventoryItems.filter { item ->
        val matchesCategory = when (selectedCategoryFilter) {
            "CRITICAL" -> item.quantity <= item.min_threshold
            "CLEANING" -> item.category.contains("Temizlik", ignoreCase = true) || item.category.contains("Cleaning", ignoreCase = true)
            "TEXTILE" -> item.category.contains("Tekstil", ignoreCase = true) || item.category.contains("Buklet", ignoreCase = true) || item.category.contains("Textile", ignoreCase = true)
            "BEVERAGE" -> item.category.contains("İçecek", ignoreCase = true) || item.category.contains("Minibar", ignoreCase = true) || item.category.contains("Beverage", ignoreCase = true)
            "FOOD" -> item.category.contains("Gıda", ignoreCase = true) || item.category.contains("Mutfak", ignoreCase = true) || item.category.contains("Food", ignoreCase = true)
            "MAINTENANCE" -> item.category.contains("Bakım", ignoreCase = true) || item.category.contains("Teknik", ignoreCase = true) || item.category.contains("Maintenance", ignoreCase = true)
            else -> true
        }
        val matchesSearch = searchQuery.isBlank() ||
                item.name.contains(searchQuery, ignoreCase = true) ||
                item.category.contains(searchQuery, ignoreCase = true)
        matchesCategory && matchesSearch
    }

    if (showAddDialog) {
        AddInventoryItemDialog(
            onDismiss = { showAddDialog = false },
            onAdd = { name, cat, qty, min, unit ->
                viewModel.addItem(name, cat, qty, min, unit)
                showAddDialog = false
            }
        )
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = onNavigateBack,
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(
                                Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = l10n("Back"),
                                tint = Color(0xFF1E293B),
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Text(
                                text = "Envanter & Stok Yönetimi",
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "Otel Depo ve Satın Alma Takibi",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        // Sub Tab Switcher (Stoklar / Satın Alma Siparişleri)
                        Surface(
                            shape = RoundedCornerShape(10.dp),
                            color = Color(0xFFF1F5F9)
                        ) {
                            Row(modifier = Modifier.padding(3.dp)) {
                                Surface(
                                    shape = RoundedCornerShape(8.dp),
                                    color = if (currentTab == 0) Color.White else Color.Transparent,
                                    shadowElevation = if (currentTab == 0) 1.dp else 0.dp,
                                    modifier = Modifier.clickable { currentTab = 0 }
                                ) {
                                    Text(
                                        text = "Stok Listesi",
                                        fontSize = 12.sp,
                                        fontWeight = if (currentTab == 0) FontWeight.Bold else FontWeight.Medium,
                                        color = if (currentTab == 0) Color(0xFF2563EB) else Color(0xFF64748B),
                                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp)
                                    )
                                }
                                Surface(
                                    shape = RoundedCornerShape(8.dp),
                                    color = if (currentTab == 1) Color.White else Color.Transparent,
                                    shadowElevation = if (currentTab == 1) 1.dp else 0.dp,
                                    modifier = Modifier.clickable { currentTab = 1 }
                                ) {
                                    Text(
                                        text = "Satın Alma",
                                        fontSize = 12.sp,
                                        fontWeight = if (currentTab == 1) FontWeight.Bold else FontWeight.Medium,
                                        color = if (currentTab == 1) Color(0xFF2563EB) else Color(0xFF64748B),
                                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp)
                                    )
                                }
                            }
                        }

                        if (currentTab == 0) {
                            Button(
                                onClick = { showAddDialog = true },
                                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                                shape = RoundedCornerShape(10.dp),
                                contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                            ) {
                                Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                                Spacer(modifier = Modifier.width(6.dp))
                                Text("Yeni Ürün Ekle", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                            }
                        }
                    }
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = {
            InventoryModuleBottomNavBar(
                selectedTab = 2,
                onNavigate = onNavigate
            )
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC))
        ) {
            if (currentTab == 0) {
                LazyColumn(
                    state = stockListState,
                    modifier = Modifier.fillMaxSize().verticalScrollbar(stockListState),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // KPI Summary Cards
                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            // Toplam Çeşit
                            Surface(
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White,
                                border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                shadowElevation = 1.dp
                            ) {
                                Column(modifier = Modifier.padding(14.dp)) {
                                    Text("Toplam Ürün Çeşidi", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text("$totalItems", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFF0F172A))
                                }
                            }

                            // Kritik Stok
                            Surface(
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White,
                                border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                shadowElevation = 1.dp
                            ) {
                                Column(modifier = Modifier.padding(14.dp)) {
                                    Text("Kritik Stok Uyarısı", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text("$criticalStockItems", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFFD97706))
                                }
                            }

                            // Tükenenler
                            Surface(
                                modifier = Modifier.weight(1f),
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White,
                                border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                                shadowElevation = 1.dp
                            ) {
                                Column(modifier = Modifier.padding(14.dp)) {
                                    Text("Tükenen Ürünler", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text("$outOfStockItems", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFFDC2626))
                                }
                            }
                        }
                    }

                    // Search & Filter Row
                    item {
                        Surface(
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White,
                            border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(12.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                OutlinedTextField(
                                    value = searchQuery,
                                    onValueChange = { searchQuery = it },
                                    placeholder = { Text("Ürün adı veya kategori ara...", fontSize = 13.sp, color = Color(0xFF94A3B8)) },
                                    leadingIcon = {
                                        Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp))
                                    },
                                    trailingIcon = {
                                        if (searchQuery.isNotEmpty()) {
                                            IconButton(onClick = { searchQuery = "" }) {
                                                Icon(Icons.Default.Clear, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(16.dp))
                                            }
                                        }
                                    },
                                    singleLine = true,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = Color(0xFF2563EB),
                                        unfocusedBorderColor = Color(0xFFE2E8F0),
                                        focusedContainerColor = Color.White,
                                        unfocusedContainerColor = Color(0xFFF8FAFC)
                                    ),
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(48.dp)
                                )

                                Spacer(modifier = Modifier.width(12.dp))

                                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                                    InvFilterChip(
                                        label = "Tümü",
                                        count = inventoryItems.size,
                                        isSelected = selectedCategoryFilter == "ALL",
                                        onClick = { selectedCategoryFilter = "ALL" }
                                    )
                                    InvFilterChip(
                                        label = "Kritik",
                                        count = criticalStockItems,
                                        isSelected = selectedCategoryFilter == "CRITICAL",
                                        selectedColor = Color(0xFFD97706),
                                        onClick = { selectedCategoryFilter = "CRITICAL" }
                                    )
                                    InvFilterChip(
                                        label = "Temizlik",
                                        count = inventoryItems.count { it.category.contains("Temizlik", true) || it.category.contains("Cleaning", true) },
                                        isSelected = selectedCategoryFilter == "CLEANING",
                                        selectedColor = Color(0xFF2563EB),
                                        onClick = { selectedCategoryFilter = "CLEANING" }
                                    )
                                    InvFilterChip(
                                        label = "Minibar",
                                        count = inventoryItems.count { it.category.contains("İçecek", true) || it.category.contains("Minibar", true) || it.category.contains("Beverage", true) },
                                        isSelected = selectedCategoryFilter == "BEVERAGE",
                                        selectedColor = Color(0xFF7C3AED),
                                        onClick = { selectedCategoryFilter = "BEVERAGE" }
                                    )
                                }
                            }
                        }
                    }

                    // Stock Item List
                    if (filteredItems.isEmpty()) {
                        item {
                            Surface(
                                modifier = Modifier.fillMaxWidth().padding(vertical = 32.dp),
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White,
                                border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
                            ) {
                                Column(
                                    modifier = Modifier.padding(36.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.Center
                                ) {
                                    Box(
                                        modifier = Modifier.size(56.dp).background(Color(0xFFEFF6FF), CircleShape),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(Icons.Default.ShoppingCart, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(28.dp))
                                    }
                                    Spacer(modifier = Modifier.height(14.dp))
                                    Text(
                                        text = "Kayıtlı Stok Ürünü Bulunmuyor",
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 16.sp,
                                        color = Color(0xFF0F172A)
                                    )
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(
                                        text = "Temizlik malzemeleri, minibar ve buklet ürünlerini takip etmek için ilk ürünü ekleyin.",
                                        fontSize = 13.sp,
                                        color = Color(0xFF64748B)
                                    )
                                    Spacer(modifier = Modifier.height(16.dp))
                                    Button(
                                        onClick = { showAddDialog = true },
                                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                                        shape = RoundedCornerShape(10.dp)
                                    ) {
                                        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(6.dp))
                                        Text("İlk Ürünü Ekle", fontSize = 13.sp)
                                    }
                                }
                            }
                        }
                    } else {
                        items(filteredItems) { item ->
                            ModernInventoryCard(
                                item = item,
                                onAddStock = { amount -> viewModel.addTransaction(item.id, "ADD", amount, "Manuel Stok Girişi") },
                                onReduceStock = { amount -> viewModel.addTransaction(item.id, "REDUCE", amount, "Manuel Stok Çıkışı") },
                                onDelete = { viewModel.deleteItem(item.id) }
                            )
                        }
                    }

                    item {
                        Spacer(modifier = Modifier.height(40.dp))
                    }
                }
            } else {
                PurchasingTab(viewModel = purchasingViewModel)
            }
        }
    }
}

@Composable
fun InvFilterChip(
    label: String,
    count: Int,
    isSelected: Boolean,
    selectedColor: Color = Color(0xFF2563EB),
    onClick: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = if (isSelected) selectedColor.copy(alpha = 0.12f) else Color(0xFFF1F5F9),
        border = androidx.compose.foundation.BorderStroke(
            1.dp,
            if (isSelected) selectedColor else Color.Transparent
        ),
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                text = label,
                fontSize = 12.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) selectedColor else Color(0xFF475569)
            )
            Box(
                modifier = Modifier
                    .clip(CircleShape)
                    .background(if (isSelected) selectedColor else Color(0xFFCBD5E1))
                    .padding(horizontal = 5.dp, vertical = 1.dp)
            ) {
                Text(
                    text = count.toString(),
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    color = if (isSelected) Color.White else Color(0xFF334155)
                )
            }
        }
    }
}

@Composable
fun ModernInventoryCard(
    item: InventoryItem,
    onAddStock: (Int) -> Unit,
    onReduceStock: (Int) -> Unit,
    onDelete: () -> Unit
) {
    var showStockDialog by remember { mutableStateOf(false) }
    var stockActionType by remember { mutableStateOf("ADD") }

    if (showStockDialog) {
        ModernStockActionDialog(
            itemName = item.name,
            type = stockActionType,
            unit = item.unit,
            onDismiss = { showStockDialog = false },
            onConfirm = { amount ->
                if (stockActionType == "ADD") onAddStock(amount) else onReduceStock(amount)
                showStockDialog = false
            }
        )
    }

    val status = when {
        item.quantity <= 0 -> InventoryStatus.OUT_OF_STOCK
        item.quantity <= item.min_threshold -> InventoryStatus.LOW_STOCK
        else -> InventoryStatus.IN_STOCK
    }

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        shadowElevation = 1.dp,
        modifier = Modifier.fillMaxWidth()
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .width(4.dp)
                    .background(status.color)
                    .align(Alignment.CenterStart)
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .background(status.bgColor),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = when {
                                item.category.contains("Temizlik", true) -> Icons.Default.Home
                                item.category.contains("İçecek", true) || item.category.contains("Minibar", true) -> Icons.Default.ShoppingCart
                                item.category.contains("Tekstil", true) -> Icons.Default.Star
                                else -> Icons.Default.Build
                            },
                            contentDescription = null,
                            tint = status.color,
                            modifier = Modifier.size(22.dp)
                        )
                    }

                    Spacer(modifier = Modifier.width(14.dp))

                    Column {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text(item.name, fontWeight = FontWeight.Bold, fontSize = 15.sp, color = Color(0xFF0F172A))
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(6.dp))
                                    .background(Color(0xFFEFF6FF))
                                    .padding(horizontal = 6.dp, vertical = 2.dp)
                            ) {
                                Text(l10n(item.category), fontSize = 11.sp, fontWeight = FontWeight.Bold, color = Color(0xFF2563EB))
                            }
                        }
                        Spacer(modifier = Modifier.height(4.dp))
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                            Text(
                                text = "Kritik Eşik: ${item.min_threshold} ${item.unit}",
                                fontSize = 12.sp,
                                color = Color(0xFF94A3B8)
                            )
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(6.dp))
                                    .background(status.bgColor)
                                    .padding(horizontal = 6.dp, vertical = 2.dp)
                            ) {
                                Text(status.text, fontSize = 11.sp, fontWeight = FontWeight.Bold, color = status.color)
                            }
                        }
                    }
                }

                // Quantity & Controls
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    Column(horizontalAlignment = Alignment.End) {
                        Text(
                            text = "${item.quantity} ${item.unit}",
                            fontWeight = FontWeight.ExtraBold,
                            fontSize = 18.sp,
                            color = status.color
                        )
                        Text(
                            text = "Mevcut Miktar",
                            fontSize = 11.sp,
                            color = Color(0xFF64748B)
                        )
                    }

                    // Stepper Actions
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(10.dp))
                            .background(Color(0xFFF1F5F9))
                            .padding(2.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(
                            onClick = {
                                stockActionType = "REDUCE"
                                showStockDialog = true
                            },
                            modifier = Modifier.size(32.dp).background(Color.White, RoundedCornerShape(8.dp))
                        ) {
                            Text("-", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = Color(0xFFDC2626))
                        }
                        Spacer(modifier = Modifier.width(4.dp))
                        IconButton(
                            onClick = {
                                stockActionType = "ADD"
                                showStockDialog = true
                            },
                            modifier = Modifier.size(32.dp).background(Color.White, RoundedCornerShape(8.dp))
                        ) {
                            Text("+", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = Color(0xFF16A34A))
                        }
                    }

                    IconButton(
                        onClick = onDelete,
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(Icons.Default.Delete, contentDescription = "Sil", tint = Color(0xFF94A3B8), modifier = Modifier.size(18.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun ModernStockActionDialog(
    itemName: String,
    type: String,
    unit: String,
    onDismiss: () -> Unit,
    onConfirm: (Int) -> Unit
) {
    var amountText by remember { mutableStateOf("1") }
    val isAdd = type == "ADD"

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Text(
                text = if (isAdd) "Stok Girişi: $itemName" else "Stok Çıkışı: $itemName",
                fontWeight = FontWeight.Bold,
                fontSize = 17.sp,
                color = Color(0xFF0F172A)
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(
                    text = if (isAdd) "Depoya eklenecek miktarı giriniz:" else "Depodan düşülecek miktarı giriniz:",
                    fontSize = 13.sp,
                    color = Color(0xFF64748B)
                )

                OutlinedTextField(
                    value = amountText,
                    onValueChange = { amountText = it },
                    label = { Text("Miktar ($unit)", fontSize = 12.sp) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = if (isAdd) Color(0xFF16A34A) else Color(0xFFDC2626),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    ),
                    modifier = Modifier.fillMaxWidth()
                )
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    val amount = amountText.toIntOrNull() ?: 1
                    onConfirm(amount)
                },
                colors = ButtonDefaults.buttonColors(containerColor = if (isAdd) Color(0xFF16A34A) else Color(0xFFDC2626)),
                shape = RoundedCornerShape(10.dp)
            ) {
                Text(if (isAdd) "Stoğa Ekle" else "Stoktan Düş", fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, shape = RoundedCornerShape(10.dp)) {
                Text("İptal", color = Color(0xFF64748B))
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddInventoryItemDialog(
    onDismiss: () -> Unit,
    onAdd: (name: String, category: String, quantity: Int, minThreshold: Int, unit: String) -> Unit
) {
    var name by remember { mutableStateOf("") }
    var quantity by remember { mutableStateOf("") }
    var minThreshold by remember { mutableStateOf("5") }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    val categories = listOf(
        "Temizlik Malzemesi",
        "Buklet / Tekstil",
        "Minibar & İçecek",
        "Mutfak & Gıda",
        "Bakım & Teknik Parça",
        "Genel Sarf Malzeme"
    )
    var selectedCategory by remember { mutableStateOf(categories.first()) }
    var categoryExpanded by remember { mutableStateOf(false) }

    val units = listOf("Adet", "Koli", "Litre", "Kg", "Paket", "Şişe")
    var selectedUnit by remember { mutableStateOf(units.first()) }
    var unitExpanded by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Yeni Envanter Ürünü Ekle", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = Color(0xFF0F172A))
                IconButton(onClick = onDismiss, modifier = Modifier.size(28.dp)) {
                    Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color(0xFF64748B))
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                // Name
                OutlinedTextField(
                    value = name,
                    onValueChange = {
                        name = it
                        errorMessage = null
                    },
                    label = { Text("Ürün Adı", fontSize = 12.sp) },
                    placeholder = { Text("Örn: Banyo Havlusu veya Çamaşır Deterjanı", color = Color(0xFF94A3B8)) },
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    ),
                    modifier = Modifier.fillMaxWidth()
                )

                // Category Dropdown
                ExposedDropdownMenuBox(
                    expanded = categoryExpanded,
                    onExpandedChange = { categoryExpanded = !categoryExpanded }
                ) {
                    OutlinedTextField(
                        value = selectedCategory,
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Kategori", fontSize = 12.sp) },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = categoryExpanded) },
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        ),
                        modifier = Modifier.menuAnchor().fillMaxWidth()
                    )
                    ExposedDropdownMenu(
                        expanded = categoryExpanded,
                        onDismissRequest = { categoryExpanded = false },
                        modifier = Modifier.background(Color.White)
                    ) {
                        categories.forEach { cat ->
                            DropdownMenuItem(
                                text = { Text(cat, fontSize = 13.sp, color = Color(0xFF0F172A)) },
                                onClick = {
                                    selectedCategory = cat
                                    categoryExpanded = false
                                }
                            )
                        }
                    }
                }

                // Quantity & Unit in Row
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    OutlinedTextField(
                        value = quantity,
                        onValueChange = { quantity = it },
                        label = { Text("Mevcut Miktar", fontSize = 12.sp) },
                        placeholder = { Text("Örn: 50", color = Color(0xFF94A3B8)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFF2563EB),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        ),
                        modifier = Modifier.weight(1f)
                    )

                    ExposedDropdownMenuBox(
                        expanded = unitExpanded,
                        onExpandedChange = { unitExpanded = !unitExpanded },
                        modifier = Modifier.weight(1f)
                    ) {
                        OutlinedTextField(
                            value = selectedUnit,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Birim", fontSize = 12.sp) },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = unitExpanded) },
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFCBD5E1),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color(0xFFF8FAFC)
                            ),
                            modifier = Modifier.menuAnchor().fillMaxWidth()
                        )
                        ExposedDropdownMenu(
                            expanded = unitExpanded,
                            onDismissRequest = { unitExpanded = false },
                            modifier = Modifier.background(Color.White)
                        ) {
                            units.forEach { u ->
                                DropdownMenuItem(
                                    text = { Text(u, fontSize = 13.sp, color = Color(0xFF0F172A)) },
                                    onClick = {
                                        selectedUnit = u
                                        unitExpanded = false
                                    }
                                )
                            }
                        }
                    }
                }

                // Critical Threshold
                OutlinedTextField(
                    value = minThreshold,
                    onValueChange = { minThreshold = it },
                    label = { Text("Kritik Stok Uyarısı Eşiği", fontSize = 12.sp) },
                    placeholder = { Text("Örn: 10", color = Color(0xFF94A3B8)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = Color(0xFF2563EB),
                        unfocusedBorderColor = Color(0xFFCBD5E1),
                        focusedContainerColor = Color.White,
                        unfocusedContainerColor = Color(0xFFF8FAFC)
                    ),
                    modifier = Modifier.fillMaxWidth()
                )

                if (errorMessage != null) {
                    Text(errorMessage ?: "", color = Color(0xFFDC2626), fontSize = 12.sp)
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    if (name.isBlank()) {
                        errorMessage = "Lütfen ürün adını giriniz."
                        return@Button
                    }
                    val qty = quantity.toIntOrNull() ?: 0
                    val min = minThreshold.toIntOrNull() ?: 5
                    onAdd(name.trim(), selectedCategory, qty, min, selectedUnit)
                },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(10.dp),
                contentPadding = PaddingValues(horizontal = 20.dp, vertical = 10.dp)
            ) {
                Text("Ürünü Ekle", fontWeight = FontWeight.Bold, color = Color.White)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, shape = RoundedCornerShape(10.dp)) {
                Text("İptal", color = Color(0xFF64748B))
            }
        }
    )
}

@Composable
fun InventoryModuleBottomNavBar(
    selectedTab: Int,
    onNavigate: (String) -> Unit = {}
) {
    Surface(
        color = Color.White,
        shadowElevation = 8.dp,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 8.dp, horizontal = 24.dp),
            horizontalArrangement = Arrangement.SpaceAround,
            verticalAlignment = Alignment.CenterVertically
        ) {
            InventoryNavItem(
                title = "Kat Hizmetleri",
                icon = Icons.Default.Home,
                isSelected = selectedTab == 0,
                onClick = { onNavigate("HousekeepingScreen") }
            )

            InventoryNavItem(
                title = "Bakım & Onarım",
                icon = Icons.Default.Build,
                isSelected = selectedTab == 1,
                onClick = { onNavigate("MaintenanceScreen") }
            )

            InventoryNavItem(
                title = "Envanter & Stok",
                icon = Icons.Default.ShoppingCart,
                isSelected = selectedTab == 2,
                onClick = { onNavigate("InventoryScreen") }
            )

            InventoryNavItem(
                title = "Vardiya Planı",
                icon = Icons.Default.Person,
                isSelected = selectedTab == 3,
                onClick = { onNavigate("ShiftPlannerScreen") }
            )
        }
    }
}

@Composable
fun InventoryNavItem(
    title: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val activeColor = Color(0xFF2563EB)
    val inactiveColor = Color(0xFF64748B)

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = if (isSelected) Color(0xFFEFF6FF) else Color.Transparent,
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = title,
                tint = if (isSelected) activeColor else inactiveColor,
                modifier = Modifier.size(18.dp)
            )
            Text(
                text = title,
                fontSize = 13.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) activeColor else inactiveColor
            )
        }
    }
}
